import {
  DEFAULT_PAD_LOOKS,
  type PadLayout,
  type PadLook,
  type PadLookMode,
  type PadLooks,
  type PadNumberStyle,
  type PadToolsAt,
} from '@zinc/shared';
import { PALETTE } from '../../lib/colours';
import {
  CentreIcon,
  CogIcon,
  ColourIcon,
  CornerIcon,
  DeleteIcon,
  DigitIcon,
  FillIcon,
  PenIcon,
  PencilIcon,
  RedoIcon,
  ResetIcon,
  SelectIcon,
  SumIcon,
  UndoIcon,
} from './icons';

export type Tool = 'digit' | 'corner' | 'centre' | 'position' | 'colour' | 'pen' | 'pencil';

interface PadProps {
  inputMode: 'sudokupad' | 'simple';
  tool: Tool;
  digits: string[];
  onTool: (tool: Tool) => void;
  onDigit: (digit: string) => void;
  onColour: (index: number) => void;
  onDelete: () => void;
  onUndo: () => void;
  onRedo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  /** SudokuPad mode's optional tools (settings, off by default). */
  showColour?: boolean;
  showPen?: boolean;
  /** "Fill all candidates" (1–9) next to redo: every digit as marks in the selected cells;
   * shown unless switched off in Settings. */
  showFill?: boolean;
  onFill?: () => void;
  /** The select tool: a toggle; while on, taps add and remove cells. Shown when `onSelecting`
   * is given (Settings can hide it). */
  selecting?: boolean;
  onSelecting?: (on: boolean) => void;
  onSettings?: () => void;
  onRestart?: () => void;
  /** The sum tool (Σ): shown when given (Settings: off by default), never greyed out;
   * `helperOn` while it's open. */
  onHelper?: () => void;
  helperOn?: boolean;
  compact?: boolean;
  /** SudokuPad mode's mark buttons (Settings: all shown by default). */
  marks?: { corner: boolean; centre: boolean; position: boolean };
  /** Blocks beside the mode buttons (default) or one row, with the buttons above or below. */
  layout?: PadLayout;
  toolsAt?: PadToolsAt;
  /** Each mode's number style and colours (Settings → Pad look). */
  looks?: PadLooks;
}

/** Which look the digit keys take while a tool is on (the colour and pen tools use the
 * digits' look). */
export function lookModeOf(tool: Tool): PadLookMode {
  return tool === 'corner' || tool === 'centre' || tool === 'position' || tool === 'pencil'
    ? tool
    : 'digit';
}

/**
 * What a digit key shows: the number big, small in the top-left corner, small in the middle,
 * or small in its own spot (1 top left, 9 bottom right), as the mark it makes sits in the cell.
 * `side` is how many spots a row of the cell has (3 for nine digits).
 */
export function KeyFace({
  digit,
  index,
  side,
  style,
  markSize,
}: {
  digit: string;
  index: number;
  side: number;
  style: PadNumberStyle;
  markSize: string;
}) {
  if (style === 'big') return <>{digit}</>;
  if (style === 'centre') return <span className={`${markSize} font-semibold`}>{digit}</span>;
  if (style === 'corner')
    return (
      <span className={`absolute top-1.5 left-2 ${markSize} leading-none font-semibold`}>
        {digit}
      </span>
    );
  const col = index % side;
  const row = Math.floor(index / side);
  return (
    <span
      className={`absolute -translate-x-1/2 -translate-y-1/2 ${markSize} leading-none font-semibold`}
      style={{ left: `${((col + 0.5) / side) * 100}%`, top: `${((row + 0.5) / side) * 100}%` }}
    >
      {digit}
    </span>
  );
}

/** A key's own colours, when the person picked any (null keeps the app's). */
export function keyColours(look: PadLook): React.CSSProperties | undefined {
  if (!look.key && !look.number) return undefined;
  return {
    ...(look.key ? { background: look.key } : {}),
    ...(look.number ? { color: look.number } : {}),
  };
}

const smallBtn =
  'flex h-10 w-[52px] items-center justify-center rounded-btn border border-line bg-surface p-0 text-ink disabled:opacity-40';

const modeBtn = (on: boolean, size = 'h-16 short:h-12') =>
  `flex ${size} items-center justify-center rounded-[12px] border p-0 ${on ? 'border-accent bg-accent' : 'border-line bg-surface'}`;

export function Pad(props: PadProps) {
  const { inputMode, tool, digits } = props;
  const marks = props.marks ?? { corner: true, centre: true, position: true };
  const row = props.layout === 'row';
  const toolsAbove = row && props.toolsAt === 'above';
  const big = digits.length > 9;
  // One row of keys (two for more than nine symbols), so narrower keys.
  const rowCols = digits.length <= 9 ? digits.length : Math.ceil(digits.length / 2);
  const keyHeight = row ? 'h-14 short:h-11' : big ? 'h-12 short:h-10' : 'h-16 short:h-12';
  const toolHeight = row ? 'h-12 short:h-10' : 'h-16 short:h-12';
  type Mode = { tool: Tool; label: string; icon: (on: boolean) => React.ReactNode };
  const sudokupadModes: Mode[] = [
    { tool: 'digit', label: 'Normal digits', icon: (on) => <DigitIcon on={on} /> },
    ...(marks.corner
      ? [{ tool: 'corner', label: 'Corner marks', icon: (on) => <CornerIcon on={on} /> } as Mode]
      : []),
    ...(marks.centre
      ? [{ tool: 'centre', label: 'Centre marks', icon: (on) => <CentreIcon on={on} /> } as Mode]
      : []),
    // Each digit in its own spot (1 top left, 9 bottom right), as Simple mode's pencil marks.
    ...(marks.position
      ? [
          {
            tool: 'position',
            label: 'Position marks',
            icon: (on) => <PencilIcon on={on} />,
          } as Mode,
        ]
      : []),
    ...(props.showColour
      ? [{ tool: 'colour', label: 'Colour', icon: (on) => <ColourIcon on={on} /> } as Mode]
      : []),
    ...(props.showPen
      ? [{ tool: 'pen', label: 'Pen', icon: (on) => <PenIcon on={on} /> } as Mode]
      : []),
  ];
  const simpleModes: Mode[] = [
    { tool: 'digit', label: 'Digits', icon: (on) => <DigitIcon on={on} /> },
    { tool: 'pencil', label: 'Pencil marks', icon: (on) => <PencilIcon on={on} /> },
  ];
  const modes = inputMode === 'simple' ? simpleModes : sudokupadModes;
  // The select toggle sits after the mark tools (SudokuPad: after the last mark shown; Simple:
  // after pencil).
  const selectAt =
    inputMode === 'simple'
      ? 2
      : modes.filter((m) => m.tool !== 'colour' && m.tool !== 'pen').length;
  // The digit keys show what a press makes (Marko, 2026-11-01): by default a big digit, a small
  // one in the top-left corner (corner marks), in the middle (centre marks) or in its own spot
  // (position marks, and Simple mode's pencil marks). Each person can change each mode's style
  // and colours (Settings → Pad look).
  const lookMode = lookModeOf(tool);
  const look = (props.looks ?? DEFAULT_PAD_LOOKS)[lookMode];
  const side = digits.length <= 4 ? 2 : digits.length <= 9 ? 3 : 4;
  const markSize = row ? 'text-[12px]' : big ? 'text-[12px]' : 'text-[15px]';
  const bigSize = row ? (big ? 'text-[19px]' : 'text-[22px]') : big ? 'text-[20px]' : 'text-[26px]';
  const selectButton = props.onSelecting ? (
    <button
      key="select"
      type="button"
      aria-label="Select cells"
      aria-pressed={!!props.selecting}
      onClick={() => props.onSelecting!(!props.selecting)}
      className={`${modeBtn(!!props.selecting, toolHeight)} ${row ? 'min-w-0 flex-1' : ''}`}
    >
      <SelectIcon on={!!props.selecting} />
    </button>
  ) : null;
  const modeButton = (m: Mode) => (
    <button
      key={m.tool}
      type="button"
      aria-label={m.label}
      aria-pressed={tool === m.tool}
      onClick={() => props.onTool(m.tool)}
      className={`${modeBtn(tool === m.tool, toolHeight)} ${row ? 'min-w-0 flex-1' : ''}`}
    >
      {m.icon(tool === m.tool)}
    </button>
  );
  // Beside the keys in blocks (two columns, one for Simple mode without select), or one row
  // above or below them.
  const toolButtons = (
    <div
      role="group"
      aria-label="Input mode"
      className={
        row
          ? 'flex gap-2'
          : inputMode === 'simple' && !selectButton
            ? 'flex w-16 shrink-0 flex-col gap-2'
            : 'grid w-32 shrink-0 grid-cols-2 content-start gap-2'
      }
    >
      {modes.slice(0, selectAt).map(modeButton)}
      {selectButton}
      {modes.slice(selectAt).map(modeButton)}
      <button
        type="button"
        aria-label={inputMode === 'simple' ? 'Erase' : 'Delete'}
        onClick={props.onDelete}
        className={`flex ${toolHeight} items-center justify-center rounded-[12px] border border-line bg-surface p-0 text-[#3D3A34] ${row ? 'min-w-0 flex-1' : ''}`}
      >
        <DeleteIcon />
      </button>
    </div>
  );

  return (
    // The whole pad, gaps included, keeps the selection when tapped (a near miss between two
    // buttons mustn't clear it): see the solve screen's tap-outside rule.
    <div className="flex flex-col gap-2.5" data-keeps-selection>
      <div className="flex gap-2">
        <button
          type="button"
          aria-label="Undo"
          disabled={!props.canUndo}
          onClick={props.onUndo}
          className={smallBtn}
        >
          <UndoIcon />
        </button>
        <button
          type="button"
          aria-label="Redo"
          disabled={!props.canRedo}
          onClick={props.onRedo}
          className={smallBtn}
        >
          <RedoIcon />
        </button>
        {props.showFill && props.onFill && (
          <button
            type="button"
            aria-label="Fill all candidates"
            onClick={props.onFill}
            className={smallBtn}
          >
            <FillIcon />
          </button>
        )}
        {props.onSettings && (
          <button
            type="button"
            aria-label="Settings"
            onClick={props.onSettings}
            className={`${smallBtn} ml-2`}
          >
            <CogIcon />
          </button>
        )}
        {props.onRestart && (
          <button
            type="button"
            aria-label="Restart puzzle"
            onClick={props.onRestart}
            className={smallBtn}
          >
            <ResetIcon />
          </button>
        )}
        {props.onHelper && (
          <button
            type="button"
            aria-label="Sum tool"
            aria-pressed={!!props.helperOn}
            onClick={props.onHelper}
            className={props.helperOn ? `${smallBtn} border-accent bg-accent text-white` : smallBtn}
          >
            <SumIcon />
          </button>
        )}
      </div>
      <div className={row ? 'flex flex-col gap-2' : 'flex gap-3'}>
        {toolsAbove && toolButtons}
        {tool === 'colour' ? (
          <div
            className={`grid min-w-0 flex-1 gap-2 ${row ? '' : 'grid-cols-3'}`}
            style={
              row ? { gridTemplateColumns: `repeat(${PALETTE.length}, minmax(0, 1fr))` } : undefined
            }
            role="group"
            aria-label="Colours"
          >
            {PALETTE.map((colour, i) => (
              <button
                key={colour}
                type="button"
                aria-label={`Colour ${i + 1}`}
                onClick={() => props.onColour(i + 1)}
                className={`${keyHeight} rounded-[12px] border border-line`}
                style={{ background: colour }}
              />
            ))}
          </div>
        ) : (
          <div
            className={`grid min-w-0 flex-1 ${row ? 'gap-1.5' : `gap-2 ${big ? 'grid-cols-4' : 'grid-cols-3'}`}`}
            style={row ? { gridTemplateColumns: `repeat(${rowCols}, minmax(0, 1fr))` } : undefined}
            role="group"
            aria-label="Digits"
            data-look={lookMode}
            data-style={look.style}
            data-layout={row ? 'row' : 'blocks'}
          >
            {digits.map((d, index) => (
              <button
                key={d}
                type="button"
                onClick={() => props.onDigit(d)}
                className={`relative ${keyHeight} rounded-[12px] border border-line bg-surface ${bigSize} font-medium text-ink`}
                style={keyColours(look)}
              >
                <KeyFace
                  digit={d}
                  index={index}
                  side={side}
                  style={look.style}
                  markSize={markSize}
                />
              </button>
            ))}
          </div>
        )}
        {!toolsAbove && toolButtons}
      </div>
    </div>
  );
}
